import { Stack, Typography, Modal, Box, Paper } from '@mui/material';
import { Frame } from '../../components/layouts/Frame';
import { DataTable } from '../../components/global/Table/DataTable';
import { Link } from '../../components/global/Link';
import { DataTableHeadCellData } from '../../components/global/Table/DataTableHeader';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAPI } from '../../hooks/useAPI';
import { ReportData } from '../../types';
import { DownloadIcon } from '../../assets/svgs/DownloadIcon';
import moment from 'moment';

const headCells: DataTableHeadCellData[] = [
  {
    id: 'job_id',
    disablePadding: true,
    label: 'Job ID',
    align: 'left',
    sortable: true,
    searchable: true,
    width: '200px',
  },
  {
    id: 'datetime',
    disablePadding: true,
    label: 'Date Time',
    align: 'left',
    cellAlign: 'left',
    sortable: true,
    searchable: true,
    width: '200px',
  },
  {
    id: 'credit_used',
    disablePadding: true,
    label: 'Credit Used',
    align: 'center',
    cellAlign: 'center',
    sortable: true,
    searchable: true,
    width: '200px',
  },
  {
    id: 'query',
    disablePadding: false,
    label: 'Query',
    align: 'left',
    cellAlign: 'left',
  },
  {
    id: 'download',
    disablePadding: false,
    label: 'Download',
    width: '50px',
  },
];

interface ReportTableData {
  id: any;
  job_id: any;
  datetime?: any;
  credit_used: any;
  query: any;
  download: JSX.Element;
}

export const Reports = () => {
  const isLoading = useRef(false);
  const [sIsLoading, setSIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { fetchData } = useAPI();
  const [rows, setRows] = useState<ReportTableData[]>([]);
  const [reports, setReports] = useState<ReportData[]>([]);
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);

  const onDownload = async (id: any) => {
    const { errors, data } = await fetchData(`/reports/download/${id}`);
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const onDownloadZip = async (id: number, link: string) => {
    const a = document.createElement('a');
    a.href = link;
    a.download = `report_${id}_image.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(link);
  };

  const refetchReports = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const { errors, data } = await fetchData('/reports');
    setReports(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  };

  const getRows = (reports: ReportData[]) => {
    const result: ReportTableData[] = [];
    for (let i = 0; i < reports.length; i++) {
      const report = reports[i];

      result.push({
        id: `${report.id}`,
        datetime: (
          <Typography>
            {moment(report.created_at).format('MM/DD/YYYY h:m:s')}
          </Typography>
        ),
        job_id: <Typography>{report.job_id}</Typography>,
        credit_used: <Typography>{report.credit_used}</Typography>,
        query: (
          <Typography>{JSON.parse(report.query ?? '[]').join(',')}</Typography>
        ),
        download: (
          <Link to="#" onClick={() => onClickRow(i, report)}>
            <DownloadIcon />
          </Link>
        ),
      });
    }
    return result;
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchReports();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setRows(getRows(reports));
  }, [reports]);

  const onClickRow = (rowIndex: number, row: any) => {
    setSelectedReport(reports[rowIndex]);
  };

  return (
    <Frame title="Reports">
      <Stack>
        <DataTable
          title="Reports"
          headCells={headCells}
          rows={rows}
          isLoading={sIsLoading}
          hideIdCheckbox={true}
          hideDeleteButton={true}
          onClickRow={onClickRow}
        />
        <Modal
          open={Boolean(selectedReport)}
          onClose={() => setSelectedReport(null)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 3,
              borderRadius: 2,
            }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Report Details
            </Typography>
            <Stack spacing={2} pt={2}>
              <Typography variant="subtitle1" fontWeight="600">
                Job ID:{' '}
                <span style={{ fontWeight: '300' }}>
                  {selectedReport?.job_id}
                </span>
              </Typography>
              <Typography variant="subtitle1" fontWeight="600">
                Request Body:
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#f4f4f4',
                  borderRadius: 1,
                  overflowX: 'auto',
                }}>
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: 14,
                  }}>
                  {JSON.stringify(
                    JSON.parse(selectedReport?.query ?? '{}'),
                    null,
                    2,
                  )}
                </Typography>
              </Paper>
              <Typography variant="subtitle1" fontWeight="600">
                Response Body:
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#f4f4f4',
                  borderRadius: 1,
                  overflowX: 'auto',
                }}>
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: 14,
                  }}>
                  {JSON.stringify(
                    JSON.parse(selectedReport?.response ?? '{}'),
                    null,
                    2,
                  )}
                </Typography>
              </Paper>
              <Typography variant="subtitle1" fontWeight="600">
                Credits Used:{' '}
                <span style={{ fontWeight: '300' }}>
                  {selectedReport?.credit_used}
                </span>
              </Typography>
              <Link
                to="#"
                onClick={() =>
                  onDownloadZip(
                    selectedReport?.id ?? 0,
                    selectedReport?.image_url ?? '#',
                  )
                }>
                <Typography color="#DA0101">Download Image (ZIP)</Typography>
              </Link>
            </Stack>
          </Box>
        </Modal>
      </Stack>
    </Frame>
  );
};
